/**
 * LlmMonitor — superadmin panel listing every LLM provider call (`llmRequests`):
 * provider/model, build context, token usage, estimated $ cost, latency, status,
 * and an on-demand view of the full prompt file. Filters by provider/model/failed
 * and shows running totals over the filtered rows. Read-only (rules: superadmin
 * read).
 */
import { useEffect, useMemo, useState } from 'react';
import type { LlmRequestDoc } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';

const usd = (n: number): string => '$' + (n < 0.01 ? n.toFixed(5) : n.toFixed(4));
const num = (n: number): string => (n || 0).toLocaleString();

/**
 * The Prompt cell — full prompts live in a PRIVATE logs bucket (the doc carries
 * a `gs://` URI, R3-30), so "view" resolves a short-lived signed READ url via
 * the superadmin-only callable and opens it in a new tab.
 */
function PromptCell({ gcsUri }: { gcsUri: string }) {
  const services = useServices();
  const [resolving, setResolving] = useState(false);
  const [failed, setFailed] = useState(false);

  // The failure mark is transient — clear it after a few seconds so stale
  // errors don't accumulate across the compact table.
  useEffect(() => {
    if (!failed) return;
    const t = setTimeout(() => setFailed(false), 4000);
    return () => clearTimeout(t);
  }, [failed]);

  if (!gcsUri) return <>—</>;

  const view = async () => {
    setResolving(true);
    setFailed(false);
    try {
      const { url } = await services.callable.resolveLlmPromptUrl({ gcsUri });
      window.open(url, '_blank', 'noopener');
    } catch {
      setFailed(true);
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
      <button className="btn btn--sm" onClick={() => void view()} disabled={resolving}>
        view
      </button>
      {failed && <span className="error-text" style={{ marginLeft: 6 }}>failed</span>}
    </>
  );
}

export function LlmMonitor() {
  const services = useServices();
  const rows = useSubscription<Array<LlmRequestDoc & { id: string }>>(
    services.subscribe.llmRequests,
    [],
    [],
  );

  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);

  const providers = useMemo(
    () => Array.from(new Set(rows.map((r) => r.provider))).sort(),
    [rows],
  );
  const models = useMemo(() => Array.from(new Set(rows.map((r) => r.model))).sort(), [rows]);

  const filtered = rows.filter(
    (r) =>
      (!provider || r.provider === provider) &&
      (!model || r.model === model) &&
      (!failedOnly || !r.ok),
  );
  const totalCost = filtered.reduce((s, r) => s + (r.estimatedCostUsd || 0), 0);
  const totalIn = filtered.reduce((s, r) => s + (r.inputTokens || 0), 0);
  const totalOut = filtered.reduce((s, r) => s + (r.outputTokens || 0), 0);

  return (
    <div className="panel" data-testid="llm-monitor">
      <h2 className="panel__title">LLM requests — monitor</h2>
      <p className="gallery__subtitle">
        Every provider call: prompt, tokens, and estimated cost (newest 300).
      </p>

      <div className="row" style={{ gap: 12, flexWrap: 'wrap', margin: '10px 0' }}>
        <select className="select" value={provider} onChange={(e) => setProvider(e.target.value)}>
          <option value="">all providers</option>
          {providers.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select className="select" value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="">all models</option>
          {models.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <label className="row" style={{ gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={failedOnly} onChange={(e) => setFailedOnly(e.target.checked)} />
          failed only
        </label>
      </div>

      <div className="row" style={{ gap: 22, margin: '6px 0 14px' }} data-testid="llm-totals">
        <span><strong>{filtered.length}</strong> calls</span>
        <span>tokens <strong>{num(totalIn)}</strong> in / <strong>{num(totalOut)}</strong> out</span>
        <span>est. cost <strong>{usd(totalCost)}</strong></span>
      </div>

      {filtered.length === 0 ? (
        <p className="muted">No LLM requests logged yet.</p>
      ) : (
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Provider / model</th>
                <th>Creation</th>
                <th>Tokens (in/out)</th>
                <th>Est. $</th>
                <th>ms</th>
                <th>Status</th>
                <th>Prompt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                  <td>{r.provider} / {r.model}</td>
                  <td>{r.creationId ? `${r.creationId.slice(0, 8)}… v${r.version}` : '—'}</td>
                  <td>{num(r.inputTokens)} / {num(r.outputTokens)}</td>
                  <td>{usd(r.estimatedCostUsd || 0)}</td>
                  <td>{r.durationMs}</td>
                  <td>{r.ok ? 'ok' : <span className="pill">error</span>}</td>
                  <td>
                    <PromptCell gcsUri={r.promptGcsUri} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
